import type { Chapter } from '@/types/nf'

export interface ChapterWordCountEstimate {
  target: number
  min: number
  max: number
}

export type ChapterWordCountDriftLevel = 'under' | 'ok' | 'over'

export interface ChapterWordCountDrift {
  level: ChapterWordCountDriftLevel
  delta: number
}

export function getNextChapterNumber(chapters: readonly Chapter[]): number {
  return chapters.reduce((max, chapter) => Math.max(max, chapter.chapter_number || 0), 0) + 1
}

export function estimateChapterWordCountRange(targetWords: number | null | undefined): ChapterWordCountEstimate | null {
  if (!Number.isFinite(targetWords) || !targetWords || targetWords < 1) return null
  const target = Math.trunc(targetWords)
  return {
    target,
    min: Math.trunc(target * 0.9),
    max: Math.trunc(target * 1.1),
  }
}

export function classifyChapterWordCountDrift(
  actualWords: number,
  estimate: ChapterWordCountEstimate | null | undefined,
): ChapterWordCountDrift | null {
  if (!estimate || !Number.isFinite(actualWords)) return null
  if (actualWords < estimate.min) return { level: 'under', delta: actualWords - estimate.target }
  if (actualWords > estimate.max) return { level: 'over', delta: actualWords - estimate.target }
  return { level: 'ok', delta: actualWords - estimate.target }
}

interface FinalizeGeneratedChapterInput {
  queryKey: readonly unknown[]
  done: { chapter_id: string }
  projectId: string
  branchId: string
  invalidateQueries: (queryKey: readonly unknown[]) => Promise<unknown>
  refetchQueries: (queryKey: readonly unknown[]) => Promise<unknown>
  selectChapter: (input: { projectId: string; branchId: string; chapterId: string }) => void
  clearGeneratingChapter: () => void
}

export async function finalizeGeneratedChapter({
  queryKey,
  done,
  projectId,
  branchId,
  invalidateQueries,
  refetchQueries,
  selectChapter,
  clearGeneratingChapter,
}: FinalizeGeneratedChapterInput): Promise<void> {
  await invalidateQueries(queryKey)
  await refetchQueries(queryKey)
  // 项目头部的字数/章节数是后端【按 main 分支实时聚合】出来的(SUM/COUNT)，
  // 不是存量字段 —— 章节一落库，项目查询也必须失效，否则头部一直停在旧值。
  //
  // ⚠️ 这一条最初被加在 ProjectDetail 的 onChapterUpdated 上，那是【错的位置】:
  // 生成走的是流式，收尾在 ChapterStreamView，根本不经过 onChapterUpdated。
  // E2E 实测：第 1 章已 done、章节树已出现节点，头部仍显示 0 字 0 章。
  await invalidateQueries(['project'])
  selectChapter({ projectId, branchId, chapterId: done.chapter_id })
  clearGeneratingChapter()
}

export async function finalizeGeneratedChapterAfterDone(input: FinalizeGeneratedChapterInput): Promise<void> {
  try {
    await finalizeGeneratedChapter(input)
  } catch (error) {
    input.clearGeneratingChapter()
    throw error
  }
}

export function chapterCompletionMessageKey(policy?: { cascade_auto_threshold: string; cascade_stop_at: string }) {
  return policy && policy.cascade_auto_threshold !== 'manual' && policy.cascade_stop_at !== 'text'
    ? 'chapter.generate.downstreamPulse'
    : 'chapter.generate.downstreamManual'
}
